import axios from "axios";
import JSZip from "jszip";
import env from "@common/environment";
import imageUtils from "@common/imageUtils";
import sdk from "@sdk";

const FRAME_COUNTS = 4;

const baseURL = env.ENV_FACE_SERVER_URL;
// const baseURL = "https://face-wrapper.alchera.ai";
const request = axios.create({
  baseURL,
  headers: {
    "Content-Type": "multipart/form-data",
    "x-ncp-apigw-api-key": env.ENV_FACE_SERVER_X_API_KEY,
  },
});

const signInUrl = env.ENV_API_URL;
// const signInUrl = `https://api.wasm-demo.alchera.ai/api`;
// const signInUrl = `${window.location.origin}`;

export const signIn = async (token: string) => {
  const response = await axios.post(`${signInUrl}/v1/auth`, { token });
  // const response = await request.post(`/v1/auth`, { token });
  return response;
};

const encrypt = (text: string) => {
  return sdk.aes256.encrypt(text);
};

/**
 * 두장의 얼굴이미지를 전송하여 얼굴의 매칭 결과를 반환한다.
 * @param {string} imageA 신분증 상의 얼굴 이미지
 * @param {string} imageB 라이브니스 촬영된 얼굴 이미지
 * @param {boolean} isEncryptMode 신분증 OCR 암호화 사용 여부
 * @param {boolean} isOCREncryptMode 얼굴 촬영 암호화 사용 여부
 */
export const compare = async (imageA: string, imageB: string, isEncryptMode?: boolean) => {
  const formData = new FormData();

  const refinedImageA = isEncryptMode ? imageUtils.stringtoBlob(encrypt(imageA)) : imageUtils.dataURItoBlob(imageA);
  const refinedImageB = isEncryptMode ? imageUtils.stringtoBlob(encrypt(imageB)) : imageUtils.dataURItoBlob(imageB);
  const imageAName = isEncryptMode ? "encrypted_image_a" : "image_a";
  const imageBName = isEncryptMode ? "encrypted_image_b" : "image_b";

  // 이미지 암호화 절차를 거친 후 비교할 이미지 A, B 를 form data 에 저장
  formData.append(imageAName, refinedImageA);
  formData.append(imageBName, refinedImageB);
  formData.append("image_b_validate", "Y");
  return request.post("/compare", formData, {
    params: { encrypt_mode: isEncryptMode },
  });
};

/**
 * 얼굴이 인식된 이미지들을 서버로 전송하여 라이브니스 여부를 검사함.
 * @param {Array<string>} livenessImages 얼굴이 인식된 base64 이미지 배열
 * @param {boolean} isEncryptMode 얼굴촬영 암호화 여부
 */
export const livenessMultiframeV2 = async (livenessImages: string[], isEncryptMode?: boolean) => {
  const ext = "jpg";
  const formData = new FormData();

  // 뽑아낸 프레임 카운트 수. (현재 multiframe v2 는 4장의 이미지를 받도록 되어있음)
  formData.append("frame_counts", FRAME_COUNTS.toString());
  formData.append("filename_extension", ext);

  // 암호화 모드시 zip 으로 압축하지 않고 각각의 암호화된 이미지를 form data 로 전송
  if (isEncryptMode) {
    // 뽑아낸 프레임 개수 만큼 이미지를 암호화
    livenessImages.forEach((item, i) => {
      const enctypred_image = imageUtils.stringtoBlob(encrypt(imageUtils.mimeTypeRemove(item))); // base64 -> encypted text -> blob
      formData.append(`encrypted_images_0${i + 1}`, enctypred_image);
    });

    return request.post("/liveness/multiframe/v2/unzip", formData, {
      params: { encrypt_mode: true },
    });
  } else {
    // 암호화 모드 미사용시 zip 으로 압축
    const zip = new JSZip();

    livenessImages.forEach((item, i) => {
      const path = `${i + 1}.jpg`;
      const data = imageUtils.dataURItoBlob(item);
      zip.file(path, data);
    });

    const zipFile = await zip.generateAsync({ type: "blob" });

    formData.append("images", zipFile, "images.zip");

    return request.post("/liveness/multiframe/v2", formData);
  }
};

/**
 * 얼굴이 인식된 이미지들을 서버로 전송하여 라이브니스 여부를 검사함.
 * @param {Array<string>} livenessImages 얼굴이 인식된 base64 이미지 배열
 * @param {boolean} isEncryptMode 얼굴촬영 암호화 여부
 */
export const livenessMultiframeV2Unzip = async (livenessImages: string[], isEncryptMode?: boolean) => {
  const ext = "jpg";
  const formData = new FormData();

  formData.append("frame_counts", FRAME_COUNTS.toString());
  formData.append("filename_extension", ext);

  livenessImages.forEach((item, i) => {
    if (isEncryptMode) {
      const enctypred_image = imageUtils.stringtoBlob(encrypt(imageUtils.mimeTypeRemove(item))); // base64 -> encypted text -> blob
      formData.append(`encrypted_images_0${i + 1}`, enctypred_image);
    } else {
      const image = imageUtils.base64toBlob(item); // base64 -> encypted text -> blob
      formData.append(`images_0${i + 1}`, image);
    }
  });

  return request.post("/liveness/multiframe/v2/unzip", formData, {
    params: { encrypt_mode: isEncryptMode },
  });
};

/**
 * 얼굴이 감지된 이미지를 전송하여 특정 비율로 크롭한 얼굴 이미지를 반환
 * @param {string} image base64로 인코딩된 이미지
 * @param {number} resizeWidth 반환될 이미지의 너비
 * @param {number} ratio 크롭 비율
 * @returns {Promise<{ image_height: number; image_width: number; image_data: string; }>}
 */
export const croppedFace = async ({
  image,
  resizeWidth,
  ratio,
  encryptMode,
}: {
  image: string;
  resizeWidth: number;
  ratio: number;
  encryptMode?: boolean;
}) => {
  const blob = encryptMode ? imageUtils.stringtoBlob(image) : imageUtils.dataURItoBlob(image);

  return request.post("/croppedface", blob, {
    headers: { "Content-Type": "image/jpeg" },
    params: { resize_width: resizeWidth, ratio, encrypt_mode: !!encryptMode },
  });
};
